import equal from 'fast-deep-equal'
import { parse as parsePath } from 'path'
import { useMemo, useSyncExternalStore } from 'react'
import { ProjectManager } from '../project'
import { ProjectFile, ProjectItem, isProjectFolder, visitProjectItems } from '../project/ProjectItem'
import { DependentDocument, Document, DocumentListener } from './Document'
import { FileDocument } from './FileDocument'
import { JSONDocument } from './JSONDocument'
import { JSONSchemaDocument } from './JSONSchemaDocument'
import { MarkdownDocument } from './MarkdownDocument'
import { NewDocument } from './NewDocument'
import { OpenAPIDocument } from './OpenAPIDocument'
import { SpectralRulesetDocument } from './SpectralRulesetDocument'

const serverSnapshot: Record<string, Document> = {}

export function useDocuments(filter?: (document: Document) => boolean) {
  const documents = useSyncExternalStore(
    (listener) => {
      DocumentStore.shared().addListener(listener)
      return () => DocumentStore.shared().removeListener(listener)
    },
    () => DocumentStore.shared().getDocuments(),
    () => serverSnapshot
  )

  return useMemo(() => {
    return filter ? Object.values(documents).filter(filter) : Object.values(documents)
  }, [documents, filter])
}

let sharedDocumentStore: DocumentStore

export class DocumentStore {
  private constructor() {}

  static shared() {
    if (!sharedDocumentStore) {
      sharedDocumentStore = new DocumentStore()
      sharedDocumentStore.reloadDocuments()
      ProjectManager.shared().addListener(() => {
        sharedDocumentStore.reloadDocuments()
      })
    }
    return sharedDocumentStore
  }

  // Documents

  private documents: Record<string, Document> = {}

  getDocuments() {
    return this.documents
  }

  // Lifecycle

  private listenersByDocument = new Map<Document, DocumentListener>()

  async reloadDocuments() {
    const currentProjectItems = ProjectManager.shared().getCurrentItems()
    const newDocuments = this.documentsFromProjectItems(currentProjectItems)

    let changed = !equal(Object.keys(this.documents).sort(), Object.keys(newDocuments).sort())
    for (const path of Object.keys(newDocuments)) {
      if (this.documents[path] && this.documents[path].constructor === newDocuments[path].constructor) {
        newDocuments[path] = this.documents[path]
      } else {
        changed = true
      }
    }

    if (changed) {
      this.documents = newDocuments

      this.listenersByDocument.forEach((listener, document) => document.removeListener(listener))
      this.listenersByDocument.clear()

      Object.entries(newDocuments).forEach(([path, document]) => {
        const listener = (change: 'text' | 'data') => {
          this.dependenciesByDocument.forEach((dependencies, dependentDocument) => {
            if (dependencies.has(path)) {
              dependentDocument.onDependencyChange()
            }
          })
          this.dispatchDocumentChange(document, change)
        }
        this.listenersByDocument.set(document, listener)
        document.addListener(listener)
      })

      const oldDependentDocuments = this.dependenciesByDocument.entries()
      for (const [oldDependentDocument, oldDependencies] of oldDependentDocuments) {
        if (Object.values(newDocuments).includes(oldDependentDocument)) {
          const newDependencies = new Set(Array.from(oldDependencies).filter((path) => path in newDocuments))
          this.dependenciesByDocument.set(oldDependentDocument, newDependencies)
        } else {
          this.dependenciesByDocument.delete(oldDependentDocument)
        }
      }

      this.dispatchChange()

      for (const document of Object.values(this.documents)) {
        this.updateDocumentType(document)
      }
    }
  }

  private documentWithPath(path: string, fileHandle: FileSystemFileHandle) {
    const { ext } = parsePath(path)
    if (ext.toLowerCase() === '.json') {
      return new JSONDocument(this, path, fileHandle, 'json')
    } else if (ext.toLocaleLowerCase() === '.yaml' || ext.toLowerCase() === '.yml') {
      return new JSONDocument(this, path, fileHandle, 'yaml')
    } else if (ext.toLocaleLowerCase() === '.md') {
      return new MarkdownDocument(this, path, fileHandle)
    } else {
      return new FileDocument(this, path, fileHandle)
    }
  }

  private documentsFromProjectItems(items: ProjectItem[]) {
    const projectFiles: ProjectFile[] = []
    visitProjectItems(items, (item, parentFolder) => {
      if (!isProjectFolder(item)) {
        projectFiles.push(item)
      }
      return false
    })

    const documents: Record<string, Document> = {}
    for (const projectFile of projectFiles) {
      const document = projectFile.fileHandle
        ? this.documentWithPath(projectFile.path, projectFile.fileHandle)
        : new NewDocument(this)
      documents[projectFile.path] = document
    }
    return documents
  }

  setDocumentType(
    document: Document,
    documentType: 'OpenAPIDocument' | 'JSONSchemaDocument' | 'SpectralRulesetDocument'
  ) {
    if (!(document instanceof JSONDocument)) {
      return
    }
    if (documentType === 'OpenAPIDocument' && !(document instanceof OpenAPIDocument)) {
      OpenAPIDocument.makeOpenAPIDocument(document)
      this.documents = { ...this.documents }
      this.dispatchChange()
    }
    if (documentType === 'JSONSchemaDocument' && !(document instanceof JSONSchemaDocument)) {
      JSONSchemaDocument.makeJSONSchemaDocument(document)
      this.documents = { ...this.documents }
      this.dispatchChange()
    }
    if (documentType === 'SpectralRulesetDocument' && !(document instanceof SpectralRulesetDocument)) {
      SpectralRulesetDocument.makeSpectralRulesetDocument(document)
      this.documents = { ...this.documents }
      this.dispatchChange()
    }
  }

  updateDocumentType(document: Document) {
    let proposedDocumentType: 'OpenAPIDocument' | 'JSONSchemaDocument' | 'SpectralRulesetDocument' | undefined
    if (document instanceof OpenAPIDocument) {
      proposedDocumentType = 'OpenAPIDocument'
    } else if (document instanceof JSONSchemaDocument) {
      proposedDocumentType = 'JSONSchemaDocument'
    } else if (document instanceof SpectralRulesetDocument) {
      proposedDocumentType = 'SpectralRulesetDocument'
    } else if (document instanceof JSONDocument) {
      if (document.data) {
        if (OpenAPIDocument.isOpenAPIData(document.data)) {
          proposedDocumentType = 'OpenAPIDocument'
        }
        if (JSONSchemaDocument.isOpenAPIData(document.data)) {
          proposedDocumentType = 'JSONSchemaDocument'
        }
        if (SpectralRulesetDocument.isSpectralRulesetData(document.data)) {
          proposedDocumentType = 'SpectralRulesetDocument'
        }
      } else {
        document
          .reloadContent()
          .then(() => {
            if (document.data) {
              this.updateDocumentType(document)
            }
          })
          .catch(console.error)
      }
    }
    if (proposedDocumentType) {
      this.setDocumentType(document, proposedDocumentType)
    }
  }

  // Dependencies

  private dependenciesByDocument = new Map<DependentDocument, Set<string>>()
  addDependency(document: DependentDocument, dependency: string) {
    const dependencies = this.dependenciesByDocument.get(document) ?? new Set()
    this.dependenciesByDocument.set(document, dependencies)

    dependencies.add(dependency)
  }

  removeDependency(document: DependentDocument, dependency: string) {
    this.dependenciesByDocument.get(document)?.delete(dependency)
  }

  removeAllDependencies(document: DependentDocument) {
    this.dependenciesByDocument.get(document)?.clear()
  }

  // Listeners

  private listeners = new Set<() => void>()
  addListener(listener: () => void) {
    this.listeners.add(listener)
  }
  removeListener(listener: () => void) {
    this.listeners.delete(listener)
  }
  dispatchChange() {
    this.listeners.forEach((listener) => listener())
  }

  private documentListeners = new Set<(document: Document, change: 'text' | 'data') => void>()
  addDocumentListener(listener: (document: Document, change: 'text' | 'data') => void) {
    this.documentListeners.add(listener)
  }
  removeDocumentListener(listener: (document: Document, change: 'text' | 'data') => void) {
    this.documentListeners.delete(listener)
  }
  dispatchDocumentChange(document: Document, change: 'text' | 'data') {
    this.documentListeners.forEach((listener) => listener(document, change))
  }
}
